import { MOCK_JOIN_LINK } from "./mockData";

// Community message ("write me an announcement") — mocked AI, FE-first. The
// manager describes what to announce in a few words; one call returns a ready
// post in the manager's app language, with shorter/warmer versions, and it is
// saved as-is.
//
// Real-API shape this mock stands in for (one POST per request):
//   { id, createdAt, brief, includeJoinLink, lang, posts: { default, shorter, warmer } }
// Scenarios are keyword-matched for the demo; the real service writes freely.

const trimBrief = (text) => text.trim().replace(/[.!?\s]+$/, "");

const SCENARIOS = [
  {
    key: "thanks",
    match: /תודה|מזל טוב|כל הכבוד|thank|congrat/i,
    he: (b) => ({
      default: `${b}.\nביחד אנחנו קהילה חזקה!`,
      shorter: `${b}!`,
      warmer: `רצינו לעצור לרגע ולהגיד: ${b}.\nזה בדיוק מה שהופך אותנו לקהילה. תודה!`,
    }),
    // The brief opens the post here, so it starts with a capital in English.
    en: (b) => ({
      default: `${b.charAt(0).toUpperCase()}${b.slice(1)}.\nTogether we're a strong community!`,
      shorter: `${b.charAt(0).toUpperCase()}${b.slice(1)}!`,
      warmer: `We wanted to stop for a moment and say: ${b}.\nThis is exactly what makes us a community. Thank you!`,
    }),
  },
  {
    key: "gathering",
    match: /אסיפ|מפגש|אירוע|ערב|מסיב|meeting|event|party|gathering/i,
    he: (b) => ({
      default: `הזמנה לכל חברי הקהילה: ${b}.\nנשמח לראות את כולם!`,
      shorter: `${b}. מחכים לכם!`,
      warmer: `חברים יקרים, יש לנו משהו כיפי: ${b}.\nזו הזדמנות מעולה להכיר את השכנים, ונשמח מאוד לראות את כולם!`,
    }),
    en: (b) => ({
      default: `An invitation for all community members: ${b}.\nWe'd love to see everyone!`,
      shorter: `${b}. See you there!`,
      warmer: `Dear neighbours, something fun is coming up: ${b}.\nIt's a great chance to meet each other, and we'd love to see everyone!`,
    }),
  },
  {
    key: "maintenance",
    match: /מים|חשמל|מעלית|עבודות|שיפוץ|תיקון|water|power|elevator|maintenance|repair/i,
    he: (b) => ({
      default: `עדכון חשוב לדיירים: ${b}.\nנעדכן כאן כשהכול יחזור לשגרה. תודה על הסבלנות!`,
      shorter: `עדכון: ${b}. תודה על הסבלנות.`,
      warmer: `שכנים יקרים, רצינו לעדכן מראש: ${b}.\nאנחנו יודעים שזה לא נוח, ונעדכן כאן ברגע שהכול יחזור לשגרה. תודה על הסבלנות וההבנה!`,
    }),
    en: (b) => ({
      default: `Important update for residents: ${b}.\nWe'll update here once everything is back to normal. Thanks for your patience!`,
      shorter: `Update: ${b}. Thanks for your patience.`,
      warmer: `Dear neighbours, a heads-up: ${b}.\nWe know it's not convenient, and we'll update here as soon as everything is back to normal. Thank you for your patience!`,
    }),
  },
];

const FALLBACK = {
  key: "general",
  he: (b) => ({
    default: `עדכון לחברי הקהילה: ${b}.\nלכל שאלה, אנחנו כאן.`,
    shorter: `עדכון: ${b}.`,
    warmer: `שלום לכולם! רצינו לשתף: ${b}.\nלכל שאלה או רעיון, אנחנו כאן בשבילכם.`,
  }),
  en: (b) => ({
    default: `An update for community members: ${b}.\nAny questions, we're here.`,
    shorter: `Update: ${b}.`,
    warmer: `Hi everyone! We wanted to share: ${b}.\nFor any question or idea, we're here for you.`,
  }),
};

const JOIN_LINK_LINE = {
  he: `עוד לא בקהילה? להצטרפות: ${MOCK_JOIN_LINK}`,
  en: `Not in the community yet? Join here: ${MOCK_JOIN_LINK}`,
};

/** Builds the saved community message. Pure — used by the call and the demo list. */
export const buildAnnouncement = ({ brief, includeJoinLink, uiLang, id, createdAt }) => {
  const lang = uiLang === "en" ? "en" : "he";
  const cleaned = trimBrief(brief);
  const scenario = SCENARIOS.find((s) => s.match.test(cleaned)) || FALLBACK;
  const posts = scenario[lang](cleaned);
  const withLink = (post) => (includeJoinLink ? `${post}\n\n${JOIN_LINK_LINE[lang]}` : post);

  return {
    id: id || `ann-${Date.now()}`,
    createdAt: createdAt || new Date().toISOString(),
    brief: brief.trim(),
    includeJoinLink: !!includeJoinLink,
    lang,
    posts: { default: withLink(posts.default), shorter: withLink(posts.shorter), warmer: withLink(posts.warmer) },
  };
};

/** Mocked AI call — resolves to the saved message after a short delay. */
export const mockAnnouncement = (request) =>
  new Promise((resolve) => setTimeout(() => resolve(buildAnnouncement(request)), 900));

// "All situations": one saved message per kind of post, with and without the join link.
const DEMO_INPUTS = [
  {
    brief: { he: "ביום שלישי תהיה הפסקת מים בבניין B בשעות הבוקר", en: "the water will be off in building B on Tuesday morning" },
    includeJoinLink: false,
  },
  {
    brief: { he: "אסיפת דיירים ביום חמישי ב-20:00 בלובי", en: "a residents' meeting on Thursday at 8 PM in the lobby" },
    includeJoinLink: true,
  },
  {
    brief: { he: "תודה לכל מי שעזר בניקיון הגינה בשבת", en: "thank you to everyone who helped clean the garden on Saturday" },
    includeJoinLink: true,
  },
  {
    brief: { he: "הוועד בוחר ספק חדש לניקיון חדר המדרגות", en: "the committee is choosing a new stairwell cleaning supplier" },
    includeJoinLink: false,
  },
];

export const buildDemoAnnouncements = (uiLang) => {
  const lang = uiLang === "en" ? "en" : "he";
  const now = Date.now();
  return DEMO_INPUTS.map((input, i) =>
    buildAnnouncement({
      brief: input.brief[lang],
      includeJoinLink: input.includeJoinLink,
      uiLang: lang,
      id: `demo-ann-${i}`,
      createdAt: new Date(now - (i + 1) * 3 * 60 * 60 * 1000).toISOString(),
    }),
  );
};
